import { useContext, useState, useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import {
  AdminCommissionReportRequest,
  CancelledOrdersReportRequest,
  DeliveryPerformanceReportRequest,
  DriverPayoutReportRequest,
  MerchantPayoutReportRequest,
  OrderSummaryReportRequest,
  PendingOrdersReportRequest,
  RefundReportRequest,
  SalesReportRequest,
} from '../client-sdk/api';

// Helper function to check if error is a "no data" scenario
const isNoDataError = async (err: any): Promise<{ isNoData: boolean; message: string }> => {
  const statusCode = err?.response?.status;
  let errorMsg = '';

  // Try to parse error message from different response formats
  if (err?.response?.data) {
    // Check if response is JSON (for 404 errors)
    if ( err.response.data instanceof Blob ) {
      try {
        const text = await err.response.data.text()
        const parsed = JSON.parse(text);
        errorMsg = parsed.message || parsed.error || err.response.data;
      } catch {
        errorMsg = err.response.data;
      }
    } else if (typeof err.response.data === 'string') {
      try {
        const parsed = JSON.parse(err.response.data);
        errorMsg = parsed.message || parsed.error || err.response.data;
      } catch {
        errorMsg = err.response.data;
      }
    } else if (err.response.data.message) {
      errorMsg = err.response.data.message;
    } else if (err.response.data.error) {
      errorMsg = err.response.data.error;
    }
  }

  // Fallback to default error message
  if (!errorMsg) {
    errorMsg = err?.message || 'An error occurred';
  }

  // Check if it's a 404 with "no data" indicators
  const isNoData =
    statusCode === 404 &&
    (errorMsg?.toLowerCase().includes('no data') ||
      errorMsg?.toLowerCase().includes('not found') ||
      errorMsg?.toLowerCase().includes('no refunds') ||
      errorMsg?.toLowerCase().includes('no commission') ||
      errorMsg?.toLowerCase().includes('no orders') ||
      errorMsg?.toLowerCase().includes('no sales') ||
      errorMsg?.toLowerCase().includes('no payouts') ||
      errorMsg?.toLowerCase().includes('no cancelled') ||
      errorMsg?.toLowerCase().includes('no pending') ||
      errorMsg?.toLowerCase().includes('no delivery') ||
      errorMsg?.toLowerCase().includes('matching the selected filters'));

  return { isNoData, message: errorMsg };
};

export const useReportApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Download Sales Report
   */
  const downloadSalesReport = useCallback(
    async (request: SalesReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadSalesReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `sales-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Cancelled Orders Report
   */
  const downloadCancelledOrdersReport = useCallback(
    async (request: CancelledOrdersReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadCancelledOrdersReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `cancelled-orders-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Refund Report
   */
  const downloadRefundReport = useCallback(
    async (request: RefundReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadRefundReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `refund-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Driver Payout Report
   */
  const downloadDriverPayoutReport = useCallback(
    async (request: DriverPayoutReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadDriverPayoutReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `driver-payout-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Merchant Payout Report
   */
  const downloadMerchantPayoutReport = useCallback(
    async (request: MerchantPayoutReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadMerchantPayoutReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `merchant-payout-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Admin Commission Report
   */
  const downloadAdminCommissionReport = useCallback(
    async (request: AdminCommissionReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadAdminCommissionReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `admin-commission-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Delivery Performance Report
   */
  const downloadDeliveryPerformanceReport = useCallback(
    async (request: DeliveryPerformanceReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadDeliveryPerformanceReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `delivery-performance-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Pending Orders Report
   */
  const downloadPendingOrdersReport = useCallback(
    async (request: PendingOrdersReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadPendingOrdersReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `pending-orders-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Download Order Summary Report
   */
  const downloadOrderSummaryReport = useCallback(
    async (request: OrderSummaryReportRequest, filename?: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadOrderSummaryReport(request, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `order-summary-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (err: any) {
        const { isNoData, message } = await isNoDataError(err);
        if (isNoData) {
          return { noData: true, message };
        }
        setError(message);
        throw { error: true, message };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    // Download methods
    downloadSalesReport,
    downloadCancelledOrdersReport,
    downloadRefundReport,
    downloadDriverPayoutReport,
    downloadMerchantPayoutReport,
    downloadAdminCommissionReport,
    downloadDeliveryPerformanceReport,
    downloadPendingOrdersReport,
    downloadOrderSummaryReport,
    // State
    loading,
    error,
  };
};

